import React from "react";
import { ymd, toMin, fmtDay, EVENT_STYLES } from "./calendarUtils";

const HOUR_PX = 48;

function layoutDay(evs) {
  const sorted = [...evs].sort((a, b) => a.start.localeCompare(b.start));
  const out = [];
  let cluster = [], cols = [], clusterEnd = "";
  const flush = () => { cluster.forEach((ev) => out.push({ ...ev, _cols: cols.length })); cluster = []; cols = []; };
  sorted.forEach((ev) => {
    if (cluster.length && ev.start >= clusterEnd) flush();
    let c = cols.findIndex((end) => end <= ev.start);
    if (c === -1) { c = cols.length; cols.push(ev.end); } else cols[c] = ev.end;
    cluster.push({ ...ev, _col: c });
    clusterEnd = cluster.length === 1 || ev.end > clusterEnd ? ev.end : clusterEnd;
  });
  flush();
  return out;
}

function EventBlock({ ev, dayStart, unitName, onClick }) {
  const top = ((toMin(ev.start) - dayStart) / 60) * HOUR_PX;
  const w = 100 / ev._cols;
  const height = Math.max(20, ((toMin(ev.end) - toMin(ev.start)) / 60) * HOUR_PX - 2);
  const kind = ev.kind === "block" ? "blocked" : ev.status;
  return (
    <button type="button" data-testid={`cal-event-${ev.id}`} onClick={() => onClick(ev)}
      style={{ top, height, left: `calc(${ev._col * w}% + 2px)`, width: `calc(${w}% - 4px)` }}
      className={`absolute overflow-hidden rounded-md border-l-4 px-1.5 py-1 text-left text-[11px] leading-tight shadow-sm transition-transform hover:z-10 hover:scale-[1.02] ${EVENT_STYLES[kind]}`}>
      <p className="font-semibold">{ev.start}–{ev.end}</p>
      <p className="truncate">{ev.kind === "block" ? (ev.reason || "Blocked") : ev.user_name}</p>
      {unitName && <p className="truncate opacity-70">{unitName}</p>}
    </button>
  );
}

export function CalendarGrid({ days, events, openMin, closeMin, unitNames, onEventClick, onSlotClick }) {
  const hours = [];
  for (let m = openMin; m < closeMin; m += 60) hours.push(m);
  const height = ((closeMin - openMin) / 60) * HOUR_PX;
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card" data-testid="calendar-grid">
      <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(${days.length > 1 ? 120 : 240}px, 1fr))` }}>
        <div className="border-b border-border" />
        {days.map((d) => (
          <div key={ymd(d)} data-testid={`cal-day-head-${ymd(d)}`}
            className={`border-b border-l border-border px-2 py-2 text-center text-xs font-semibold ${ymd(d) === ymd(new Date()) ? "text-primary" : ""}`}>
            {fmtDay(d)}
          </div>
        ))}
        <div className="relative" style={{ height }}>
          {hours.map((m) => (
            <span key={m} className="absolute right-2 -translate-y-1/2 font-mono text-[10px] text-muted-foreground" style={{ top: ((m - openMin) / 60) * HOUR_PX }}>
              {m > openMin ? `${String(m / 60).padStart(2, "0")}:00` : ""}
            </span>
          ))}
        </div>
        {days.map((d) => {
          const key = ymd(d);
          return (
            <div key={key} className="relative border-l border-border" style={{ height }} data-testid={`cal-day-${key}`}>
              {hours.map((m) => (
                <div key={m} onClick={() => onSlotClick(key, m)}
                  className="absolute inset-x-0 cursor-pointer border-t border-border/60 hover:bg-primary/5"
                  style={{ top: ((m - openMin) / 60) * HOUR_PX, height: HOUR_PX }} />
              ))}
              {layoutDay(events.filter((e) => e.date === key)).map((ev) => (
                <EventBlock key={ev.id} ev={ev} dayStart={openMin} unitName={unitNames?.[ev.unit_id]} onClick={onEventClick} />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
